import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Archive, ArchiveRestore, FolderKanban, Loader2, Building, Palette,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { legalAPI } from '@/lib/api';
import { useTranslation } from '@/i18n/TranslationContext';
import { cn } from '@/lib/utils';

export default function CategoriesPage() {
  const { t, language } = useTranslation();
  const [sectors, setSectors] = useState([]);
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', methodology: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [secRes, thRes, catRes] = await Promise.all([
        legalAPI.listSectors(true),
        legalAPI.listThemes(),
        legalAPI.listCategories(showArchived),
      ]);
      setSectors(secRes.data.sectors || []);
      setThemes(thRes.data.themes || []);
      setCategories(catRes.data.categories || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [showArchived]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditingCat(null); setForm({ title: '', description: '', methodology: '' }); setDialogOpen(true); };
  const openEdit = (cat) => { setEditingCat(cat); setForm({ title: cat.title || '', description: cat.description || '', methodology: cat.methodology || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editingCat) {
        await legalAPI.updateCategory(editingCat.category_id, form);
        toast.success('Updated');
      } else {
        await legalAPI.createCategory(form);
        toast.success('Created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleArchive = async (cat) => {
    try {
      await legalAPI.archiveCategory(cat.category_id);
      toast.success(cat.is_archived ? 'Restored' : 'Archived');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('categories')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('manage_categories')}</p>
        </div>
        <Button onClick={openCreate} data-testid="create-category-button">
          <Plus className="mr-2 h-4 w-4" />{t('new_category')}
        </Button>
      </div>

      <Tabs defaultValue="sectors" className="space-y-4">
        <TabsList className="bg-muted/30 border border-border/60">
          <TabsTrigger value="sectors" className="data-[state=active]:bg-accent/40">
            <Building className="mr-2 h-4 w-4" />Sectors
          </TabsTrigger>
          <TabsTrigger value="themes" className="data-[state=active]:bg-accent/40">
            <Palette className="mr-2 h-4 w-4" />Themes
          </TabsTrigger>
        </TabsList>

        {/* SECTORS TAB */}
        <TabsContent value="sectors">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sectors.map(s => {
              const name = language === 'es' ? (s.name_es || s.name_en || s.name || '') : (s.name_en || s.name || '');
              const desc = language === 'es' ? (s.description_es || s.description_en || s.description || '') : (s.description_en || s.description || '');
              return (
                <Card key={s.id} className={cn('border-border/60 transition-[border-color,box-shadow] duration-200 hover:border-border/80', s.is_universal && 'border-primary/30')}>
                  <CardContent className="p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-base font-semibold text-foreground">{name}</p>
                      {s.is_universal && <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">Universal</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    {s.display_order !== undefined && <p className="text-[9px] text-muted-foreground/50">Order: {s.display_order}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* THEMES TAB */}
        <TabsContent value="themes">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {themes.map(th => {
              const name = language === 'es' ? (th.name_es || th.name_en || th.name || '') : (th.name_en || th.name || '');
              const desc = language === 'es' ? (th.description_es || th.description_en || th.description || '') : (th.description_en || th.description || '');
              return (
                <Card key={th.id} className="border-border/60 transition-[border-color,box-shadow] duration-200 hover:border-border/80">
                  <CardContent className="p-5 space-y-2">
                    <p className="text-base font-semibold text-foreground">{name}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    {th.display_order !== undefined && <p className="text-[9px] text-muted-foreground/50">Order: {th.display_order}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCat ? t('edit_category') : t('create_category')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t('category_title')}</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="category-title-input" className="bg-muted/30 border-border" /></div>
            <div className="space-y-2"><Label>{t('category_description')}</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-muted/30 border-border min-h-[80px]" /></div>
          </div>
          <DialogFooter><Button onClick={handleSave} data-testid="category-save-button">{t('save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
